import { useState, useRef, useEffect } from "react"
import { Check, ChevronDown, Search } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"



const SearchableSelect = ({ options, value, onChange, placeholder, icon, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Filter options based on search term
  const filteredOptions = options.filter((option) => option.name?.toLowerCase().includes(searchTerm?.toLowerCase()))

  // Get selected option
  const selectedOption = options.find((option) => option.value === value)

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
        className={`${disabled ? 'cursor-not-allowed' : ''} w-full flex items-center justify-between px-4 h-10 border border-neutral-200  rounded-xl bg-white dark:border-neutral-800 dark:bg-neutral-900 text-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-1 focus:ring-yellow-500 dark:focus:ring-yellow-600 transition-all`}
      >
        <div className="flex items-center gap-2 truncate">
          {icon && !selectedOption?.flag && <span className="text-neutral-500 dark:text-neutral-400">{icon}</span>}
          {selectedOption?.flag && <img src={selectedOption?.flag} alt={selectedOption?.name} className="h-3" />}
          <span className="text-xs truncate">{selectedOption?.name || placeholder}</span>
        </div>
        <ChevronDown
          size={16}
          className={`text-neutral-500 dark:text-neutral-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
            }`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-[9999] mt-1 w-full bg-white dark:border-neutral-800 dark:bg-neutral-900 border border-neutral-200  rounded-xl shadow-lg overflow-hidden"
            style={{ minWidth: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-2">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" />
                <input
                  type="text"
                  className="w-full h-10 pl-9 pr-3 text-xs bg-neutral-50 dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-xl focus:outline-none focus:ring-1 focus:ring-yellow-500 dark:focus:ring-yellow-600 focus:border-transparent text-neutral-900 dark:text-neutral-200"
                  placeholder={`Search ${placeholder.toLowerCase()}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            <div className="max-h-60 overflow-y-auto px-2 py-1">
              {filteredOptions.map((option) => (
                <button
                  key={option.id}
                  className={`w-full text-left px-4 py-2 rounded-xl text-xs hover:bg-neutral-50 dark:hover:bg-neutral-700 ${value === option.value
                    ? "bg-yellow-50 text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400"
                    : "text-neutral-700 dark:text-neutral-300"
                    }`}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onChange(option.value)
                    setIsOpen(false)
                    setSearchTerm("")
                  }}
                >
                  <div className="flex items-center justify-between">
                    <p className="flex justify-start items-center gap-2">{option?.flag && <img src={option?.flag} alt={option?.name} className="h-3" />} <span>{option.name}</span></p>
                    {value === option.value && <Check size={16} className="text-yellow-600 dark:text-yellow-400" />}
                  </div>
                </button>
              ))}
              {filteredOptions.length === 0 && (
                <div className="px-4 py-2 text-xs text-neutral-500 dark:text-neutral-400">No results found</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default SearchableSelect